import React, { useState, useEffect, useRef } from 'react';
import { 
    Button, 
    Textarea, 
    Spinner, 
    RadioGroup, 
    Radio,
    Label,
    makeStyles,
    tokens,
    shorthands
} from '@fluentui/react-components';
import { SendRegular, DeleteRegular } from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { acquireAccessToken } from '../../utils/authToken';
import Configuration from '../../config/einvoicing-configuration';
import { ErrorState } from '../../components/common/ErrorState';
import './ChatPage.css';
import { constructStandaloneChatUrl } from '../../controller/dashboardController';
import { usePageTitle } from '../../hooks/usePageTitle';

interface Message {
    role: string;
    content: string;
    timestamp?: Date;
}

const useStyles = makeStyles({
    header: {
        ...shorthands.margin('0'),
    },
    root: {
        marginTop: '20px',
    },
    chatModeToggle: {
        marginBottom: '16px',
    },
    chatContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 220px)', // Adjusted to make room for the toggle
        maxHeight: '780px',
        width: '100%',
        ...shorthands.padding('0', '20px'),
    },
    messagesContainer: {
        flexGrow: 1,
        overflowY: 'auto',
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.padding('16px'),
        marginBottom: '16px',
        backgroundColor: tokens.colorNeutralBackground2,
        minHeight: '400px',
    },
    inputContainer: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.padding('10px'),
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        marginBottom: '20px',
    },
    messageInput: {
        flexGrow: 1,
        marginRight: '10px',
    },
    sendButton: {
        minWidth: '80px',
    },
    userMessage: {
        textAlign: 'right',
        marginBottom: '16px',
    },
    assistantMessage: {
        textAlign: 'left',
        marginBottom: '16px',
    },
    messageBubble: {
        display: 'inline-block',
        ...shorthands.padding('10px', '16px'),
        ...shorthands.borderRadius('12px'),
        maxWidth: '85%',
        boxShadow: tokens.shadow4,
    },
    userBubble: {
        backgroundColor: tokens.colorBrandBackground2,
    },
    assistantBubble: {
        backgroundColor: tokens.colorNeutralBackground1,
    },
    messageText: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    timestamp: {
        fontSize: '11px',
        color: tokens.colorNeutralForeground2,
        marginTop: '4px',
    },
    label: {
        marginTop: '10px', 
        marginBottom: '10px', 
        fontSize: '18px', 
        fontWeight: '600'
    },
    disclaimer: {
        fontSize: '12px', 
        color: tokens.colorNeutralForeground2, 
        marginBottom: '10px'
    },
    modeDescription: {
        fontSize: '13px', 
        color: tokens.colorNeutralForeground2, 
        marginTop: '4px'
    }
});

function ChatPage() {
usePageTitle('E-Invoicing Assistant');
const [chatMessages, setChatMessages] = useState<Message[]>([{
        role: 'assistant',
        content: 'Hello! I\'m your E-Invoicing assistant. How can I help you today?',
        timestamp: new Date()
    }]);
    const [currentMessage, setCurrentMessage] = useState('');
    const [isChatLoading, setIsChatLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [accessToken, setAccessToken] = useState('');
    const [chatMode, setChatMode] = useState('document');
    const [inputError, setInputError] = useState<string | null>(null);
    const { instance, accounts } = useMsal();
    const messagesEndRef = useRef<null | HTMLDivElement>(null);

    const styles = useStyles();

    // Scroll to bottom whenever messages change
    useEffect(() => {
        scrollToBottom();
    }, [chatMessages]);

    const scrollToBottom = () => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        messagesEndRef.current?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    };

    async function getAccessToken() {
        if (accessToken === '') {
            const token = await acquireAccessToken(instance, accounts[0]);
            setAccessToken(token);
            return token;
        }
        return accessToken;
    }

    const sendChatMessage = async () => {
        if (currentMessage.trim() === '') {
            setInputError('Please enter a message before sending. Type your question about e-invoicing processes, troubleshooting, or document analysis.');
            return;
        }
        setInputError(null);

        // Add user message to chat
        const userMessage: Message = {
            role: 'user',
            content: currentMessage,
            timestamp: new Date()
        };

        const updatedMessages = [...chatMessages, userMessage];
        setChatMessages(updatedMessages);
        setCurrentMessage('');
        setIsChatLoading(true);

        try {
            // Construct URL to fetch chat response
            const chatUrl = constructStandaloneChatUrl(Configuration.eventServiceUrl);
            const token = await getAccessToken();

            // Filter out timestamps before sending to API
            const messagesToSend = updatedMessages.map(({ role, content }) => ({ role, content }));

            const requestParams = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    messages: messagesToSend,
                    chatMode: chatMode
                })
            };

            const result = await fetch(chatUrl, requestParams);

            if (result !== undefined && result.ok) {
                const assistantMessage = await result.json();
                // Add timestamp to assistant message
                assistantMessage.timestamp = new Date();
                setChatMessages([...updatedMessages, assistantMessage]);
                setIsError(false);
            } else {
                // Handle error
                setChatMessages([
                    ...updatedMessages,
                    {
                        role: 'assistant',
                        content: 'Sorry, I encountered an error while processing your request.',
                        timestamp: new Date()
                    }
                ]);
                setIsError(true);
            }
        } catch (error) {
            console.error("Error sending chat message:", error);
            setChatMessages([
                ...updatedMessages,
                {
                    role: 'assistant',
                    content: 'Sorry, I encountered an error while processing your request.',
                    timestamp: new Date()
                }
            ]);
            setIsError(true);
        } finally {
            setIsChatLoading(false);
        }
    };

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendChatMessage();
        }
    };

    const handleChatModeChange = (ev: any, data: any) => {
        if (data.value) {
            setChatMode(data.value);
            // Reset chat when changing modes
            const welcomeMessage = data.value === 'icm'
                ? "I'll help you with ICM-related questions. Please provide the ICM ID or describe your issue."
                : data.value === 'document'
                    ? "I'm ready to analyze your documents. Please describe what you'd like to know about your e-invoicing document."
                    : "Hello! I'm your E-Invoicing assistant. How can I help you today?";

            setChatMessages([{
                role: 'assistant',
                content: welcomeMessage,
                timestamp: new Date()
            }]);
        }
    };

    const clearChat = () => {
        // Reset chat when changing modes
        const welcomeMessage = chatMode === 'icm'
            ? "I'll help you with ICM-related questions. Please provide the ICM ID or describe your issue."
            : chatMode === 'document'
                ? "I'm ready to analyze your documents. Please describe what you'd like to know about your e-invoicing document."
                : "Hello! I'm your E-Invoicing assistant. How can I help you today?";

            setChatMessages([{
            role: 'assistant',
            content: welcomeMessage,
            timestamp: new Date()
        }]);
    };

    return (
        <div className={styles.root}>
            <h2 className={styles.label}>E-Invoicing Assistant</h2>
            <div className={styles.disclaimer}>
                This is an AI-powered conversation. Ask questions about e-invoicing processes, troubleshooting, or document analysis.
            </div>

            <div className={styles.chatModeToggle}>
                <Label>Select conversation type:</Label>
                <RadioGroup value={chatMode} onChange={handleChatModeChange} layout="horizontal">
                    <Radio value="icm" label="ICM Related" />
                    <Radio value="document" label="Document Analysis" />
                </RadioGroup>
                {chatMode === 'icm' && (
                    <div className={styles.modeDescription}>
                        Provide an ICM number and describe your ICM-related issue.
                    </div>
                )}
                {chatMode === 'document' && (
                    <div className={styles.modeDescription}>
                        Provide your document ID(s) and ask questions about them.
                    </div>
                )}
            </div>

            <div className={styles.chatContainer}>
                <div className={styles.messagesContainer}>
                    {chatMessages.map((msg, index) => (
                        <div
                            key={index}
                            className={msg.role === 'user' ? styles.userMessage : styles.assistantMessage}
                        >
                            <div
                                className={`${styles.messageBubble} ${msg.role === 'user' ? styles.userBubble : styles.assistantBubble
                                    }`}
                            >
                                <div className={styles.messageText}>
                                    {msg.content}
                                </div>
                                {msg.timestamp && (
                                    <div className={styles.timestamp}>
                                        {formatTime(msg.timestamp)}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    {isChatLoading && (
                        <div style={{ textAlign: 'center', padding: '16px' }} role="status" aria-live="polite">
                            <Spinner size="small" label="AI is thinking..." />
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {inputError && (
                    <div role="alert" aria-live="assertive" style={{ padding: '8px 10px', backgroundColor: tokens.colorPaletteRedBackground1, borderRadius: '4px', marginBottom: '4px', fontSize: '13px', color: tokens.colorPaletteRedForeground1 }}>
                        {inputError}
                    </div>
                )}
                <div className={styles.inputContainer}>
                    <Textarea
                        placeholder="Type your message..."
                        value={currentMessage}
                        resize="none"
                        className={styles.messageInput}
                        onChange={(_, data) => {
                            setCurrentMessage(data.value || '');
                            if (inputError && (data.value || '').trim() !== '') {
                                setInputError(null);
                            }
                        }}
                        onKeyDown={handleKeyDown}
                        disabled={isChatLoading}
                        aria-invalid={inputError ? true : undefined}
                        aria-errormessage={inputError ? "chat-input-error" : undefined}
                        style={{
                            flexGrow: 1,
                            marginRight: '10px',
                            minHeight: '36px',
                            maxHeight: '100px',
                            fontSize: '14px'
                        }}
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                            icon={<SendRegular />}
                            onClick={sendChatMessage}
                            disabled={currentMessage.trim() === '' || isChatLoading}
                            className={styles.sendButton}
                        >
                            Send
                        </Button>
                        <Button
                            icon={<DeleteRegular />}
                            onClick={clearChat}
                            disabled={chatMessages.length <= 1 || isChatLoading}
                            className={styles.sendButton}
                            appearance="secondary"
                            aria-label="Clear chat"
                        />
                    </div>
                </div>
            </div>

            {isError && (
                <ErrorState
                    description="Check your internet connection or try again later."
                    title="There was an error communicating with the assistant"
                />
            )}
        </div>
    );
}

export default ChatPage;